import React from 'react';
import { Heading, Text } from '../../common/Typography';
import { Card } from '../../common/Card';
import Button from '../../common/Button';
import { StatusBadge } from '../../common';
import Tabs from '../../common/Tabs';
import ClubLogo from '../../clubs/ClubLogo';
import PlayerSearchInput from '../../common/PlayerSearchInput';
import { getSessionStatusLabel } from '../hub/sessionHubUtils';

export const WRAP_UP_TABS = [
  { id: 'details', label: 'Details' },
  { id: 'player-management', label: 'Payments' },
  { id: 'analysis', label: 'Analysis' },
  { id: 'games', label: 'Games' },
  { id: 'session-ranking', label: 'Rankings' },
  { id: 'player-interactions', label: 'Interactions' },
  { id: 'reopen', label: 'Reopen' },
];

export const WRAP_UP_TAB_IDS = WRAP_UP_TABS.map((tab) => tab.id);

export const resolveWrapUpTab = (tabParam) => {
  if (tabParam === 'session') return 'reopen';
  return WRAP_UP_TAB_IDS.includes(tabParam) ? tabParam : 'player-management';
};

export const SessionWrapUpPlayerFilter = ({
  filterPlayers,
  selectedFilterPlayer,
  onSelectPlayer,
  onRemovePlayer,
  filterPlayersLoading,
  filterPlayersError,
  isAdmin,
  className = '',
}) => (
  <div className={`border-b border-border-medium pb-4 mb-4 ${className}`}>
    <Text className="text-xs font-medium text-text-secondary mb-2">Filter by player</Text>
    <PlayerSearchInput
      placeholder="Search players…"
      availablePlayers={filterPlayers}
      selectedPlayer={selectedFilterPlayer}
      onSelectPlayer={onSelectPlayer}
      onRemovePlayer={onRemovePlayer}
      showSelectedInfo
      className="w-full max-w-md"
      includePaymentAliases={isAdmin}
    />
    {filterPlayersLoading ? (
      <Text className="mt-2 text-xs text-text-muted">Loading players…</Text>
    ) : null}
    {filterPlayersError ? (
      <Text className="mt-2 text-xs text-danger">{filterPlayersError}</Text>
    ) : null}
  </div>
);

export const SessionWrapUpTabNav = ({ activeTab, onTabChange }) => (
  <Tabs
    tabs={WRAP_UP_TABS}
    activeTab={activeTab}
    onTabChange={onTabChange}
  />
);

export const SessionWrapUpHeader = ({
  session,
  selectedClub,
  formatDate,
  formatTime,
  getStatusVariant,
  isAdmin,
  updating,
  paymentsComplete,
  requiresPaymentWrapUp = true,
  onComplete,
  onArchive,
  onGoToPayments,
}) => {
  const status = session.session_status;
  const isOngoing = status === 'ongoing';
  const isCompleted = status === 'completed';
  const needsPayments = isCompleted && requiresPaymentWrapUp && !paymentsComplete;
  const canArchive = isCompleted && paymentsComplete;

  const headerAction = !isAdmin
    ? null
    : isOngoing
      ? {
          label: 'Complete session',
          onClick: onComplete,
          disabled: updating,
        }
      : needsPayments
        ? {
            label: 'Go to payments',
            onClick: onGoToPayments,
            disabled: false,
          }
        : canArchive
          ? {
              label: 'Archive session',
              onClick: onArchive,
              disabled: updating,
            }
          : null;

  return (
    <div className="mb-4 space-y-4 sm:mb-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          {selectedClub ? (
            <ClubLogo
              logoImageUrl={selectedClub.logo_image_url}
              clubName={selectedClub.name}
              size="md"
              className="border border-border-medium shadow-brand-sm"
            />
          ) : null}
          <div className="min-w-0 flex-1">
            <Text className="text-xs font-medium uppercase tracking-wide text-text-muted">
              Review & archive
            </Text>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <Heading level={1} className="truncate text-xl font-bold text-text-primary sm:text-2xl">
                {session.name}
              </Heading>
              <StatusBadge variant={getStatusVariant(status)} size="small">
                {status === 'completed' ? 'Session Finished' : getSessionStatusLabel(status)}
              </StatusBadge>
            </div>
            <Text className="mt-1 text-sm text-text-secondary">
              {formatDate(session.session_date)} · {formatTime(session.start_time)}
              {session.session_types?.name ? ` · ${session.session_types.name}` : ''}
            </Text>
          </div>
        </div>
        {headerAction ? (
          <Button
            variant="primary"
            className="w-full shrink-0 sm:w-auto"
            disabled={headerAction.disabled}
            onClick={headerAction.onClick}
          >
            {headerAction.label}
          </Button>
        ) : null}
      </div>
    </div>
  );
};

export const SessionWrapUpStatusPanel = ({
  session,
  updating,
  onComplete,
  onReopen,
}) => {
  const status = session.session_status;
  const isCompleted = status === 'completed';
  const isArchived = status === 'archived';
  const isOngoing = status === 'ongoing';
  const canReopen = isCompleted || isArchived;

  return (
    <Card variant="default" size="large" className="!p-4 sm:!p-6">
      <Heading level={3} className="mb-1">
        Reopen
      </Heading>
      <Text className="mb-5 text-sm text-text-secondary">
        Reopen if you still need to change courts or details.
      </Text>

      {isOngoing ? (
        <div className="mb-5 rounded-xl border border-border-medium border-l-4 border-l-info bg-surface-subtle/50 p-4">
          <Text className="text-sm font-medium text-text-primary">Still live</Text>
          <Text className="mt-0.5 text-xs text-text-secondary">
            Complete the session when courts have finished, then archive from the top of the page.
          </Text>
          <Button
            variant="primary"
            size="small"
            className="mt-3"
            disabled={updating}
            onClick={onComplete}
          >
            Complete session
          </Button>
        </div>
      ) : null}

      {isArchived ? (
        <div className="mb-5 rounded-xl border border-border-medium border-l-4 border-l-border-medium bg-surface-subtle/50 p-4">
          <Text className="text-sm font-medium text-text-primary">Archived</Text>
          <Text className="mt-0.5 text-xs text-text-secondary">
            This session is locked as history. Reopen it if you need to adjust payments or details
            before archiving again.
          </Text>
        </div>
      ) : null}

      {canReopen ? (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <Text className="text-sm font-medium text-text-primary">Reopen session</Text>
            <Text className="mt-0.5 text-xs text-text-secondary">
              {isArchived
                ? 'Moves it back to finished so you can edit details or payments.'
                : 'Puts the session back on court so you can keep playing.'}
            </Text>
          </div>
          <Button
            variant="outline"
            size="small"
            className="w-full shrink-0 sm:w-auto"
            disabled={updating}
            onClick={onReopen}
          >
            Reopen session
          </Button>
        </div>
      ) : null}
    </Card>
  );
};
